<script setup>
import { CsIcon } from '../icon';
import { tv } from 'tailwind-variants';
import { computed, onMounted, ref, useSlots } from 'vue';

/**
 * @typedef {object} TagProps
 * @property {boolean} [isClosable=true] - Whether the tag is closable
 * @property {number} [maxWidth=200] - The maximum width of the tag
 * @property {boolean} [isShowTooltip=false] - Whether to show a tooltip when the tag is truncated or ellipsis
 * @property {'default' | 'caution'} [variantColor=default] - The color variant of the tag
 */

/**
 * @type {TagProps}
 */
const props = defineProps({
  isClosable: {
    type: Boolean,
    default: true,
  },
  maxWidth: {
    type: Number,
    default: 200,
  },
  isShowTooltip: {
    type: Boolean,
    default: false,
  },
  variantColor: {
    type: String,
    default: 'default',
  },
});

const emit = defineEmits(['remove']);

const slots = useSlots();

const contentEl = ref(null);
const isEllipsis = ref(false);

function hasSlot(name) {
  return !!slots[name];
}

const tagRecipe = tv({
  slots: {
    root: [
      'group',
      'h-5 rounded-lg',
      'inline-flex items-center justify-center gap-1 pl-1.5 py-0.5 text-xs font-medium',
      'cursor-default',
    ],
    content: [
      'overflow-hidden text-ellipsis whitespace-nowrap',
      'max-w-[var(--max-width)]',
    ],
    closeButton: [
      'flex items-center justify-center cursor-pointer text-brand-gray-12 hover:bg-brand-gray-9 hover:text-brand-gray-15 active:bg-brand-gray-10 h-[18px] w-[18px] rounded-[7px]',
    ],
  },
  variants: {
    variantColor: {
      default: {
        root: 'bg-brand-gray-8 dark:bg-brand-gray-14 text-brand-text',
      },
      caution: {
        root: 'bg-brand-surface-caution-active text-brand-text',
        content: 'text-brand-yellow-14',
      },
    },
    isClosable: {
      true: {
        root: 'pr-[1px]',
      },
      false: {
        root: 'pr-1.5',
      },
    },
    hasIcon: {
      true: {
        root: 'pl-[1px]',
      },
    },
  },
  defaultVariants: {
    isClosable: false,
    variantColor: 'default',
  },
});

const tagClasses = computed(() =>
  tagRecipe({
    ...props,
    hasIcon: hasSlot('icon'),
  })
);

const getSanitizedContent = () => {
  const content = contentEl.value?.textContent || '';
  return content.trim().replace(/<[^>]*>?/g, '');
};

onMounted(() => {
  // Temporarily remove max-width constraint
  contentEl.value.style.maxWidth = 'none';
  // Get the natural width
  const naturalWidth = contentEl.value.offsetWidth;
  // Restore max-width constraint
  contentEl.value.style.maxWidth = '';

  // Compare natural width with max-width
  isEllipsis.value = naturalWidth > props.maxWidth;
});

defineExpose({
  contentEl,
  isEllipsis,
});
</script>

<template>
  <span
    :style="{ '--max-width': `${props.maxWidth}px` }"
    data-component="CsTag"
    :class="tagClasses.root()"
  >
    <slot v-if="hasSlot('icon')" name="icon" />

    <span
      ref="contentEl"
      v-cs-tooltip="isEllipsis && isShowTooltip ? getSanitizedContent() : ''"
      :class="tagClasses.content()"
    >
      <slot />
    </span>

    <button
      v-if="props.isClosable"
      v-cs-tooltip="'Remove'"
      :class="tagClasses.closeButton()"
      @click="emit('remove')"
    >
      <CsIcon name="x" class="h-4 w-4" />
    </button>
  </span>
</template>
